<template>
    <div class="container pt-8 update-plan-container">
        <h2 class="pause--heading" style="text-align: center" v-text="$t('PAUSE_UPDATE_YOUR_PLAN')"></h2>
        <p class="text-center mb-4 mt-4">{{ $t('UPDATE_PLAN_MESSAGE') }}</p>
        <multi-teaser :fields="fields"/>
        <hr class="divider">
        <!-- product -->
        <div class="align-left">
            <p v-html="$t('RESTART_PAGE_SELECT_BOX')"></p>
            <p>{{$t('SELECT_BOX_TYPE')  }}</p>
        </div>
        <div class="selectBox">
            <select-box
                v-if="subscription && availableProducts.length"
                v-model="selectedProduct"
                :options="availableProducts"
                :showPop="true"
                :selected="defaultProductSelected"/>
            <loader v-else/>
        </div>
        <hr class="divider">
        <!-- plans -->
        <div class="align-left" id="freq">
            <h6>{{ $t('PAUSE_CHANGE_FREQUENCY') }}</h6>
            <p v-text="$t('PAUSE_DELIVERIES_RESTART_FREQUENCY')"></p>
            <radio-field
                v-for="(item, index) in planOptions"
                :key="index"
                v-model="selectedPlan"
                :option="item.plan_slug"
                :isCurrentPlan="item.isCurrentPlan"
                :defaultText="$t('CURRENT_PLAN')"
                :label="item.plan_name"/>
        </div>
        <p v-if="showErr" class="err">{{ $t('ERR_FOR_LITE_BOX') }}</p>
        <hr class="divider">
        <div class="align-left">
            <h6>{{ $t('PAUSE_SKIP_UNTIL') }}</h6>
            <p>{{ $t('PAUSE_SELECT_DATE_TO_RESUME') }}</p>
        </div>

        <div class="calendar-outer-box">
            <Calendar
                class="calendar-container"
                :customStyle=customStyle
                :deliverable-days="allDeliveries"
                :controlLimit="10"
                :lockTooltipText="$t('CALENDAR_TOO_LATE_TO_MAKE_CHANGES')"
                :isPauseFlow="true"
                :allowMonthToggling="true"/>
        </div>

        <div class="btn-container">
            <button @click.prevent="changeFrequency" class="btn btn--primary btn--half-width restart-date--confirm">
                <span v-if="processing" v-text="$t('SUBMIT_BUTTON_PLEASE_WAIT')"/>
                <span v-else v-text="$t('PAUSE_DELIVERIES_RESTART_DATE_CONFIRM')"/>
            </button>
            <button v-text="continueToPauseText"
                    @click.prevent="setStage" class="btn btn--secondary-black btn--half-width restart-date--reject">
            </button>

            <div class="mt-8 mb-12" style="cursor: pointer">
                <a @click.prevent="closePauseDeliveries">
                    <u>{{ $t('PAUSE_BACK')}}</u>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { Calendar, MultiTeaser } from '@freddiesflowers/frontend-component-library';
import { formatDate } from '@/helpers/DateHelpers';
import * as Dates from '@/constants/Dates';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import SubscriptionService from '@/services/subscriptions/SubscriptionService';
import { CSPageEvents } from '@/helpers/GAHelpers';
import RadioField from '@/components/forms/fields/RadioField';
import SelectBox from '@/components/forms/register/SelectBox';
import Loader from '@/components/structure/Loader';

let subscriptionService = new SubscriptionService();

export default {
    name: 'UpdateYourPlan',
    components: {
        Calendar,
        MultiTeaser,
        RadioField,
        SelectBox,
        Loader,
    },
    data() {
        return {
            processing: false,
            selectedPlan: null,
            showErr: false,
            selectedRestartDate: null,
            schedulePlanChange: false,
            errorHandler: new ErrorResponseHandler(),
            stageId: 'restart',
            address: null,
        };
    },
    mounted() {
        this.fetchData();
        this.$store.dispatch('register/getAllProducts');
        this.$root.$on('selected-date', (payload) => {
            this.selectedRestartDate = payload;
        });
    },
    computed: {
        ...mapGetters({
            allDeliveries: 'scheduledOrder/getAllDeliveries',
            subscription: 'subscription/getActiveSubscription',
            plans: 'subscription/getPlans',
            deliveries: 'scheduledOrder/getDeliveries',
            productFamilies: 'subscription/getProductFamilies',
            isEssentialEnabled: 'account/getEssentialBoxRestart',
            isMonthlySubscriptionEnabled: 'account/isMonthlySubscriptionEnabled',
            defaultProductFamily: 'register/getDefaultProductFamily',
            primaryProducts: 'register/getPrimaryProducts',
            isPauseFlow: 'account/getIsPauseFlow',
            hasFetchedProducts: 'register/getHasFetchedProducts',
            getProductTypes: 'subscription/getProductTypes',
            getAddressById: 'address/getAddressById',
            fields: 'account/getFieldsData',
        }),
        subscriptionId() {
            return this.subscription?.id;
        },
        currentPlan() {
            return this.subscription?.plan_slug;
        },
        customStyle() {
            return {
                container: {
                    width: '350px',
                },
                dayWrapper: {
                    height: '49px',
                    width: '52px',
                },
                dayText: {
                    height: '35px',
                    width: '35px',
                    fontSize: '16px',
                },
            };
        },
        continueToPauseText() {
            return this.isPauseFlow ? this.$t('PAUSE_DELIVERIES_NO_THANKS') : this.$t('CANCEL_DELIVERIES_NO_THANKS');
        },
        allowedDeliveries() {
            return this.deliveries.filter((item) => !item.is_locked && !item.scheduled_order_id)
                .map((item) => {
                    item.human = formatDate(new Date(item.datetime), Dates.DAY_DATE_MONTH_YEAR);
                    return item;
                });
        },
        subscriptionDetails() {
            return this.$store.getters['subscription/getSubscriptionById'](this.subscriptionId);
        },
        currentPlanFrequency() {
            return this.subscriptionDetails?.plan_slug;
        },
        defaultProductSelected() {
            return (this.hasFetchedProducts && this.primaryProducts?.filter(item => item.product_family.toLowerCase() === this.subscription?.product_family.toLowerCase())[0]) || null;
        },
        availableProducts() {
            return this.hasFetchedProducts && this.primaryProducts.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        },
        selectedProduct: {
            get() {
                return this.$store.getters['register/getSelectedProductDetails'];
            },
            set(value) {
                this.$store.commit('register/setSelectedProductDetails', value);
            },
        },
        planOptions() {
            this.plans.map(item => {
                if (item.plan_slug === this.currentPlan) {
                    item.isCurrentPlan = true;
                } else {
                    item.isCurrentPlan = false;
                }
            });
            return this.selectedProduct?.product_family === 'lite'
                ? this.plans.filter(item => !['monthly', 'three-weekly'].includes(item.plan_slug))
                : this.plans;
        },
    },
    methods: {
        fetchData() {
            this.$store.dispatch('subscription/fetchAllSubscriptions', true);
            this.$store.dispatch('address/fetchAllAddresses', true);
            this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
        },
        pauseWithRestartDate() {
            CSPageEvents(`${this.stageId}-accepted`);
            this.processing = true;
            let payload = {
                reason: 'Restart Step',
                feedback: null,
                resumeDate: this.selectedRestartDate,
                planSlug: this.selectedPlan,
                primary_product_id: this.selectedProduct?.product_id,
            };
            subscriptionService.pauseSubscription(this.subscriptionId, payload)
                .then(() => {
                    this.handleDeliveriesPaused();
                    this.processing = false;
                })
                .catch(error => {
                    this.processing = false;
                    this.errorHandler.setResponse(error.response.data);
                });
        },

        closePauseDeliveries() {
            return this.$router.go(-1);
        },

        upgradeBox() {
            let selectedProduct = this.getProductTypes.filter(item => item.product_family === this.selectedProduct.product_family);
            this.$store.dispatch('subscription/updateProductType', {
                subId: this.subscriptionId,
                prodId: selectedProduct[0].id,
                name: this.selectedBoxName })
                .then(() => {
                    this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
                })
                .catch((error) => {
                    this.processing = false;
                    this.errorHandler.setResponse(error.response.data);
                });
        },

        changeFrequency() {
            if (this.selectedProduct.product_family === 'lite' && !['weekly', 'fortnightly'].includes(this.selectedPlan)) {
                this.showErr = true;
                this.$scrollTo('#freq');
                return;
            }

            if (this.selectedProduct) this.upgradeBox();
            subscriptionService.updateSubscriptionPlan(
                this.subscriptionId,
                this.selectedPlan,
                this.allowedDeliveries[0].datetime,
                this.schedulePlanChange,
                this.selectedRestartDate)
                .then(() => {
                    this.handleSubscriptionUpdated();
                    this.$store.dispatch('subscription/fetchAllSubscriptions', true);
                    this.$store.dispatch('scheduledOrder/fetchAllDeliveries', true);
                }).catch(error => {
                    this.processing = false;
                    this.showErr = false;
                    this.errorHandler.setResponse(error.response.data);
                });
            CSPageEvents(`${this.stageId}-paused`);
        },

        setStage() {
            this.$router.push({ name: 'account.pause-or-cancel' });
            // this.$emit('updateStage', 'pause-reason');
        },

        handleDeliveriesPaused() {
            this.$store.commit('account/setShowFloatingBanner', true);
            this.$store.commit('account/setBannerMessage', this.$t('PAUSE_DELIVERIES_CONFIRMATION_MODAL_YOUR_DELIVERIES'));
            setTimeout(() => {
                this.$store.commit('account/setShowFloatingBanner', false);
                this.$store.commit('account/setBannerMessage', null);
            }, 5000);
            this.fetchData();
            return this.$router.go(-1);
        },

        handleSubscriptionUpdated() {
            this.$store.commit('account/setShowFloatingBanner', true);
            this.$store.commit('account/setBannerMessage', this.$t('UPDATED_DELIVERIES_CONFIRMATION_MODAL_YOUR_DELIVERIES'));
            setTimeout(() => {
                this.$store.commit('account/setShowFloatingBanner', false);
                this.$store.commit('account/setBannerMessage', null);
            }, 5000);
            this.fetchData();
            return this.$router.go(-1);
        },
    },
    watch: {
        subscription: {
            deep: true,
            handler(newValue) {
                let deliveryAddressId = this.subscription?.delivery_address_id;
                if (deliveryAddressId) {
                    this.address = this.getAddressById(deliveryAddressId);
                }
                let currentPlan = newValue?.plan_slug;
                this.selectedPlan = this.plans.filter(item => item.plan_slug === currentPlan)[0]?.plan_slug;
            },
        },
        address: {
            deep: true,
            handler(newAddress) {
                let postcode = newAddress.postcode;
                if (postcode && !this.getProductTypes.length) this.$store.dispatch('subscription/fetchAvailableDeliveryDay', postcode);
            },
        },
        selectedPlan: {
            handler() {
                if (this.showErr) this.showErr = false;
            },
        },
    },
};
</script>

<style scoped>
    .update-plan-container {
        max-width: 303px;
        margin: 0 auto;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    .selectBox {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 375px;
        margin: 30px 0px;
    }

    .align-left {
        align-self: flex-start;
        text-align: left;
        margin: 24px 0px;
    }

    .calendar-outer-box {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background-color: #F6F6F6;
        padding: 15px;
        width: 375px;
    }

    .btn-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
    }

    .restart-date {
        margin: 8px 0;
    }
    .restart-date--label {
        font-size: 18px;
    }
    .restart-date--confirm {
        margin: 54px 0 0;
        background: #262323;
        height: 40px;
        padding-top: 0px;
        padding-bottom: 0px;
        width: 100%;
    }
    .restart-date--reject {
        margin: 10px 0 0px;
        height: 40px;
        padding-top: 0px;
        padding-bottom: 0px;
        width: 100%;
    }

    .divider {
        width: 100%;
        border: 0.75px solid #CECECE;
    }

@media (min-width: 740px) {
        .update-plan-container, .selectBox {
            max-width: 612px;
        }
        .btn-container {
            display: block;
            width: 100%;
        }

        .calendar-outer-box {
            width: 100%;
        }

        .restart-date--confirm {
            max-width: 220px;
        }
        .restart-date--reject {
            max-width: 360px;
        }
    }

    .err {
        color: red;
    }
</style>
